// routes/AppRoutes.jsx
import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { Box, Button, Heading, Layer, Text } from "grommet";
import PrivateRoute from "../components/PrivateRoute";
import ManagerRoute from "../components/ManagerRoute";
import AdminRoute from "../components/AdminRoute";
import QuizRoutes from "./QuizRoutes";
import ManagerRoutes from "./ManagerRoutes";
import CommonRoutes from "./CommonRoutes";
import Error404 from "../pages/Error404";
import { QuizProvider } from "../contexts/QuizContext";
import UserRoutes from "./UserRoutes";
import AdminRoutes from "./AdminRoutes";
import PursuitAccessRoutes from "./PursuitAccessRoutes";
import PursuitAccessRoute from "../components/PursuitAccessRoute";

const RateLimitModal = ({ retryAt, onClose }) => {
  const getSecondsLeft = () =>
    retryAt ? Math.max(0, Math.ceil((retryAt - Date.now()) / 1000)) : null;

  const [secondsLeft, setSecondsLeft] = useState(getSecondsLeft);

  useEffect(() => {
    if (!retryAt) return;
    const interval = setInterval(() => {
      const left = getSecondsLeft();
      setSecondsLeft(left);
      if (left <= 0) clearInterval(interval);
    }, 1000);
    return () => clearInterval(interval);
  }, [retryAt]);

  const formatTime = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  };

  return (
    <Layer onEsc={onClose} onClickOutside={onClose}>
      <Box pad="large" align="center" gap="medium" width="medium">
        <Heading level="3" margin="none">Too Many Requests</Heading>
        <Text textAlign="center">Rate limit exceeded. Please try again later.</Text>
        {secondsLeft !== null && secondsLeft > 0 && (
          <Text textAlign="center" color="status-warning">
            Please try again in <strong>{formatTime(secondsLeft)}</strong>.
          </Text>
        )}
        {secondsLeft === 0 && (
          <Text textAlign="center" color="status-ok">You can try again now.</Text>
        )}
        {secondsLeft === null && (
          <Text textAlign="center">Please wait a moment and try again.</Text>
        )}
        <Button
          label={secondsLeft === 0 ? "Close" : "Please wait..."}
          onClick={onClose}
          primary
          disabled={secondsLeft !== null && secondsLeft > 0}
        />
      </Box>
    </Layer>
  );
};

// Must be inside <Router> to use useNavigate
const RateLimitListener = () => {
  const [rateLimitInfo, setRateLimitInfo] = useState(null);

  useEffect(() => {
    const handler = (e) => {
      const { retryAt } = e.detail;
      setRateLimitInfo({ retryAt: retryAt || null });
    };
    window.addEventListener("app:rate-limited", handler);
    return () => window.removeEventListener("app:rate-limited", handler);
  }, []);

  if (!rateLimitInfo) return null;

  return (
    <RateLimitModal
      retryAt={rateLimitInfo.retryAt}
      onClose={() => setRateLimitInfo(null)}
    />
  );
};

const AppRoutes = () => {
  return (
    <Router>
      <QuizProvider>
        <RateLimitListener />
        <Routes>
          <Route element={<PrivateRoute />}>
            {QuizRoutes}
            {UserRoutes}
          </Route>

          <Route element={<ManagerRoute />}>{ManagerRoutes}</Route>
          <Route element={<AdminRoute />}> {AdminRoutes}</Route>
          <Route element={<PursuitAccessRoute />}> {PursuitAccessRoutes}</Route>

          {CommonRoutes}
          <Route path="*" element={<Error404 />} />
        </Routes>
      </QuizProvider>
    </Router>
  );
};

export default AppRoutes;
